/**
 * Supabase Authentication Context
 * Provides auth functionality for consumer, prosumer, and enterprise users
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase, getUserScenario, handleAuthError, UserScenario } from '../lib/supabase';
import { betaAuthService } from '../services/BetaAuthService';
import { initializeAuth, writeSessionHint, clearSessionHint } from '../lib/sessionRestore';
import { profileService } from '../services/ProfileService';
import { clearAllAuthStorage, cleanupIfCorrupted } from '../lib/storageCleanup';

interface AuthContextType {
  // User and session
  user: User | null;
  session: Session | null;
  loading: boolean;
  sessionReady: boolean;
  error: string | null;
  scenario: UserScenario;
  userRole: 'user' | 'creator' | 'admin';
  foundersCircle: boolean;

  // Auth methods
  signUp: (email: string, password: string, metadata?: any) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithOAuth: (provider: 'google' | 'github' | 'kakao' | 'linkedin') => Promise<void>;
  signInWithMagicLink: (email: string) => Promise<void>;
  signInWithPhone: (phone: string) => Promise<void>;
  verifyPhoneOtp: (phone: string, token: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;

  // User profile
  updateProfile: (data: { firstName?: string; lastName?: string }) => Promise<void>;

  // Beta platform integration
  isBetaAuthenticated: boolean;
  betaUserEmail: string | null;

  // Helpers
  clearError: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const SupabaseAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Check if auth is disabled for development
  const authDisabled = import.meta.env.VITE_DISABLE_AUTH === 'true';
  const mockUser = import.meta.env.VITE_MOCK_USER === 'true';
  
  // Create mock user for development (using valid UUID format)
  const mockUserData = authDisabled && mockUser ? {
    id: '00000000-0000-0000-0000-000000000001', // Valid UUID for development testing
    email: 'dev@redheli.com',
    user_metadata: {
      first_name: 'Dev',
      last_name: 'User'
    }
  } as unknown as User : null;
  
  const [user, setUser] = useState<User | null>(null);  // Start null, will be set by getSession
  const [session, setSession] = useState<Session | null>(null);
  const [sessionReady, setSessionReady] = useState(false); // Track if session is initialized
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isBetaAuthenticated, setIsBetaAuthenticated] = useState(false);
  const [betaUserEmail, setBetaUserEmail] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<'user' | 'creator' | 'admin'>('user');
  const [foundersCircle, setFoundersCircle] = useState<boolean>(false);

  useEffect(() => {
    // SECURITY: Check for orphaned auth data immediately on startup
    console.log('[SupabaseAuthContext] 🔍 Checking for storage corruption on startup...');
    const wasCorrupted = cleanupIfCorrupted();
    if (wasCorrupted) {
      console.log('[SupabaseAuthContext] ✅ Cleaned orphaned auth data - user should log in');
    }

    // Timeout fallback to prevent infinite loading (max 10 seconds)
    // Increased from 5s to allow OAuth callback to complete exchangeCodeForSession()
    const loadingTimeout = setTimeout(() => {
      console.log('[SupabaseAuthContext] Loading timeout reached, forcing loading=false and sessionReady=true');

      // Don't call getSession() here - it might hang just like initializeAuth()!
      // Just set sessionReady=true and let the useEffect that watches [user?.id, sessionReady]
      // handle calling fetchUserRole with the user that was set by the SIGNED_IN event
      setLoading(false);
      setSessionReady(true);

      console.log('[SupabaseAuthContext] ✅ sessionReady is now true, useEffect will fetch role if user exists');
    }, 10000);

    // Check for beta authentication first
    const betaAuth = localStorage.getItem('beta_authenticated');
    const betaEmail = localStorage.getItem('beta_user_email');

    if (betaAuth === 'true' && betaEmail) {
      setIsBetaAuthenticated(true);
      setBetaUserEmail(betaEmail);

      // DISABLED: All users migrated to Supabase, no longer checking Beta sessions
      // This prevents CSP violations trying to connect to beta.redheli.com
      /*
      betaAuthService.checkBetaSession(betaEmail).then((status) => {
        if (!status.authenticated) {
          // Beta session expired, clear it
          localStorage.removeItem('beta_authenticated');
          localStorage.removeItem('beta_user_email');
          setIsBetaAuthenticated(false);
          setBetaUserEmail(null);
        }
      }).catch((err) => {
        // Silently fail - don't block auth flow
        console.log('[SupabaseAuthContext] Beta session check failed (non-blocking):', err.message);
      });
      */
    }

    // Skip if Supabase is not configured
    if (!import.meta.env.VITE_SUPABASE_URL) {
      console.log('[SupabaseAuthContext] No Supabase URL, setting loading=false');
      clearTimeout(loadingTimeout);
      setLoading(false);
      return;
    }

    // SECURITY: Initialize auth with session restore (in-memory tokens)
    console.log('[SupabaseAuthContext] Initializing secure session...');
    initializeAuth().then(async (sessionRestored) => {
      console.log('[SupabaseAuthContext] Session restore result:', sessionRestored);
      clearTimeout(loadingTimeout);

      // Get current session (either restored or new)
      const { data: { session } } = await supabase.auth.getSession();

      setSession(session);
      const finalUser = session?.user ?? (authDisabled && mockUser ? mockUserData : null);
      console.log('[SupabaseAuthContext] Setting user to:', finalUser?.id || 'null');
      setUser(finalUser);

      // Small delay to ensure session is fully written to localStorage before marking ready
      // This prevents race condition where ProfileService reads before Supabase writes
      await new Promise(resolve => setTimeout(resolve, 100));

      setSessionReady(true); // ✅ Mark session as initialized
      console.log('[SupabaseAuthContext] ✅ Session ready, user can make authenticated queries');
      setLoading(false);

      // Sync with existing Neon database if user exists (non-blocking background sync)
      if (session?.user) {
        syncUserWithNeonDB(session.user).catch((err) => {
          console.log('[SupabaseAuthContext] Background DB sync failed (non-blocking):', err);
        });
      }
    }).catch((error) => {
      console.error('[SupabaseAuthContext] Session initialization error:', error);

      // Clean up corrupted storage on initialization failure
      console.log('[SupabaseAuthContext] 🔧 Checking for storage corruption...');
      cleanupIfCorrupted();

      clearTimeout(loadingTimeout);
      setSessionReady(true); // Set ready even on error to allow app to continue
      setLoading(false);
    });

    // Listen for auth changes.
    // supabase-js awaits this callback while it holds its auth lock — including during startup,
    // when it refreshes a stored session. Awaiting any Supabase call in here (getSession() in
    // storeSessionHint did) deadlocks: startup never finishes, so exchangeCodeForSession() on
    // /auth/callback times out. Keep the callback synchronous; defer anything that calls Supabase.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log('Auth state changed:', _event, session?.user?.email);
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Handle auth events
      switch (_event) {
        case 'SIGNED_IN':
          if (session?.user) {
            writeSessionHint(session);  // ✅ Store hint for session restore (no Supabase call)

            // Background sync (non-blocking), after the callback has returned
            const signedInUser = session.user;
            setTimeout(() => {
              syncUserWithNeonDB(signedInUser).catch((err) => {
                console.log('[SupabaseAuthContext] Background DB sync failed (non-blocking):', err);
              });
            }, 0);
          }
          break;
        case 'SIGNED_OUT':
          // Clear any local data
          clearSessionHint();  // ✅ Clear session restore hint
          localStorage.removeItem('journey_progress');
          localStorage.removeItem('beta_authenticated');
          localStorage.removeItem('beta_user_email');
          setIsBetaAuthenticated(false);
          setBetaUserEmail(null);
          // Also logout from beta
          betaAuthService.logoutFromBeta();
          break;
        case 'TOKEN_REFRESHED':
          // Update session hint with new expiry
          if (session?.user) writeSessionHint(session);
          break;
        case 'USER_UPDATED':
          console.log('User profile updated');
          break;
      }
    });

    return () => {
      clearTimeout(loadingTimeout);
      subscription.unsubscribe();
    };
  }, []);

  // Fetch user role and founders circle status from profiles table
  // Uses ProfileService with session guards and retry logic
  const fetchUserRole = async (userId: string) => {
    try {
      console.log('[SupabaseAuthContext] 🔍 Fetching role for user:', userId);

      // Check if session is ready before attempting query
      if (!sessionReady) {
        console.warn('[SupabaseAuthContext] ⏳ Session not ready yet, deferring role fetch');
        // Role will be fetched when session becomes ready via useEffect
        return;
      }

      // CRITICAL: Add small delay to ensure session is fully persisted to localStorage
      // This prevents race condition where ProfileService reads before Supabase writes
      console.log('[SupabaseAuthContext] ⏳ Waiting for session persistence...');
      await new Promise(resolve => setTimeout(resolve, 200));

      // Get the access token from the current session to avoid timeout issues
      const accessToken = session?.access_token;
      if (!accessToken) {
        console.error('[SupabaseAuthContext] ❌ No access token in session');
        setUserRole('user');
        setFoundersCircle(false);
        return;
      }

      // Use ProfileService with the access token from the session we already have
      // This avoids the getSession() timeout issue
      console.log('[SupabaseAuthContext] ✅ Passing access token to ProfileService');
      const { data, error } = await profileService.getProfile(userId, accessToken);

      if (error) {
        console.error('[SupabaseAuthContext] ❌ Error fetching user role:', error);

        // Use ProfileService's intelligent error handling
        const defaultRole = profileService.handleProfileError(error);
        setUserRole(defaultRole as 'user' | 'creator' | 'admin');
        setFoundersCircle(false);

        // Handle specific error cases
        if (error.message?.includes('JWT') || error.message?.includes('token')) {
          console.error('[SupabaseAuthContext] 🔐 Auth token issue detected');
          console.log('[SupabaseAuthContext] 🔧 Cleaning corrupted auth storage...');
          clearAllAuthStorage();
          console.log('[SupabaseAuthContext] ✅ Storage cleaned - user should re-authenticate');
        }

        return;
      }

      // Success - set role and founders circle status
      if (data) {
        // Handle array-based roles: determine primary role for backward compatibility
        const roles = data.user_roles || [];
        let primaryRole: 'user' | 'creator' | 'admin' = 'user';

        // Priority: admin > creator > user
        if (roles.includes('admin')) {
          primaryRole = 'admin';
        } else if (roles.includes('creator')) {
          primaryRole = 'creator';
        } else if (roles.includes('user')) {
          primaryRole = 'user';
        }

        console.log('[SupabaseAuthContext] ✅ User roles loaded:', roles, '→ Primary role:', primaryRole);
        setUserRole(primaryRole);
        setFoundersCircle(data.founders_circle || false);
      } else {
        console.warn('[SupabaseAuthContext] ⚠️ No profile data returned, defaulting to user role');
        setUserRole('user');
        setFoundersCircle(false);
      }
    } catch (error) {
      console.error('[SupabaseAuthContext] 💥 Unexpected exception fetching user role:', error);
      setUserRole('user');
      setFoundersCircle(false);
    }
  };

  // Load user role whenever user changes or session becomes ready
  useEffect(() => {
    if (user?.id && sessionReady) {
      console.log('[SupabaseAuthContext] Loading role for user:', user.id);
      fetchUserRole(user.id);
    } else if (!user?.id) {
      // Reset to default when user logs out
      setUserRole('user');
      setFoundersCircle(false);
    }
    // If user exists but session not ready, wait for sessionReady to trigger this effect
  }, [user?.id, sessionReady]);

  // Sync Supabase user with backend
  const syncUserWithNeonDB = async (supabaseUser: User) => {
    try {
      // Call API to sync user (Option A: Supabase auth only)
      const response = await fetch('/api/auth/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          supabaseId: supabaseUser.id,
          email: supabaseUser.email,
          metadata: supabaseUser.user_metadata,
        }),
      });

      if (!response.ok) {
        console.error('[SupabaseAuthContext] Failed to sync user with backend:', response.status);
      } else {
        console.log('[SupabaseAuthContext] ✅ User synced with backend');
      }

      // Role will be fetched by useEffect when sessionReady + user.id changes
      // No need to fetch here - prevents stale closure bug
    } catch (error) {
      console.error('[SupabaseAuthContext] Error syncing user:', error);
    }
  };

  // Sign up new user
  const signUp = async (email: string, password: string, metadata?: any) => {
    try {
      setError(null);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: metadata?.firstName,
            last_name: metadata?.lastName,
            scenario: getUserScenario(email, metadata),
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) throw error;

      // Email confirmation disabled - user can sign in immediately
      console.log('[SupabaseAuthContext] User signed up successfully:', data.user?.email);
    } catch (error: any) {
      setError(handleAuthError(error));
      throw error;
    }
  };

  // Sign in with email/password
  const signIn = async (email: string, password: string) => {
    try {
      setError(null);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
    } catch (error: any) {
      // On auth failure, check for corrupted storage
      console.log('[SupabaseAuthContext] 🔧 Sign-in failed, checking for storage corruption...');
      const wasCorrupted = cleanupIfCorrupted();

      if (wasCorrupted) {
        console.log('[SupabaseAuthContext] ✅ Cleaned corrupted storage - user should retry login');
        setError('Your session data was corrupted and has been cleaned. Please try logging in again.');
      } else {
        setError(handleAuthError(error));
      }

      throw error;
    }
  };

  // Sign in with OAuth provider
  const signInWithOAuth = async (provider: 'google' | 'github' | 'kakao' | 'linkedin') => {
    try {
      setError(null);
      // Map 'linkedin' to 'linkedin_oidc' for Supabase
      const supabaseProvider = provider === 'linkedin' ? 'linkedin_oidc' : provider;
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: supabaseProvider as any,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          scopes: provider === 'github' ? 'read:user user:email' : undefined,
        },
      });

      if (error) throw error;
    } catch (error: any) {
      setError(handleAuthError(error));
      throw error;
    }
  };

  // Sign in with magic link
  const signInWithMagicLink = async (email: string) => {
    try {
      setError(null);
      const { data, error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) throw error;
      
      setError('Check your email for the login link!');
    } catch (error: any) {
      setError(handleAuthError(error));
      throw error;
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      console.log('[SUPABASE AUTH] Signing out...');
      setError(null);

      // Sign out from Supabase with timeout to prevent hanging
      // Use Promise.race to enforce 3-second timeout
      const signOutPromise = supabase.auth.signOut({ scope: 'global' });
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Sign out timeout')), 3000)
      );

      try {
        const { error } = await Promise.race([signOutPromise, timeoutPromise]) as any;
        if (error) {
          console.warn('[SUPABASE AUTH] ⚠️ Sign out returned error (will clear anyway):', error);
        }
      } catch (timeoutError) {
        console.warn('[SUPABASE AUTH] ⚠️ Sign out timed out (will clear local state anyway)');
      }

      // CRITICAL: Small delay to ensure Supabase has cleared server-side session
      await new Promise(resolve => setTimeout(resolve, 100));

      // Clear all storage completely
      localStorage.clear();
      sessionStorage.clear();

      // Clear state
      setUser(null);
      setSession(null);
      setUserRole('user');
      setFoundersCircle(false);
      setSessionReady(false);

      console.log('[SUPABASE AUTH] ✅ Sign out successful (local state cleared)');

      // Force redirect to login page to ensure clean state
      console.log('[SUPABASE AUTH] 🔄 Redirecting to login page...');
      window.location.href = '/login';
    } catch (error: any) {
      console.error('[SUPABASE AUTH] ❌ Sign out error:', error);
      // Even if sign out errors, clear local state and redirect
      localStorage.clear();
      sessionStorage.clear();
      setUser(null);
      setSession(null);
      setUserRole('user');
      setFoundersCircle(false);
      setSessionReady(false);
      setError(handleAuthError(error));

      // Force redirect even on error
      window.location.href = '/login';
    }
  };

  // Sign in with phone OTP
  const signInWithPhone = async (phone: string) => {
    try {
      setError(null);
      const { data, error } = await supabase.auth.signInWithOtp({
        phone,
      });
      
      if (error) throw error;
      setError('Check your phone for the verification code!');
    } catch (error: any) {
      setError(handleAuthError(error));
      throw error;
    }
  };

  // Verify phone OTP
  const verifyPhoneOtp = async (phone: string, token: string) => {
    try {
      setError(null);
      const { data, error } = await supabase.auth.verifyOtp({
        phone,
        token,
        type: 'sms',
      });
      
      if (error) throw error;
    } catch (error: any) {
      setError(handleAuthError(error));
      throw error;
    }
  };

  // Reset password
  const resetPassword = async (email: string) => {
    try {
      console.log('[SUPABASE AUTH] ========================================');
      console.log('[SUPABASE AUTH] PASSWORD RESET INITIATED');
      console.log('[SUPABASE AUTH] Email:', email);
      console.log('[SUPABASE AUTH] Redirect URL:', `${window.location.origin}/auth/callback`);
      console.log('[SUPABASE AUTH] Current origin:', window.location.origin);
      console.log('[SUPABASE AUTH] ========================================');

      setError(null);
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/callback`,
      });

      console.log('[SUPABASE AUTH] Supabase response received');
      console.log('[SUPABASE AUTH] Data:', data);
      console.log('[SUPABASE AUTH] Error:', error);

      if (error) {
        console.error('[SUPABASE AUTH] ❌ Password reset ERROR:', error);
        console.error('[SUPABASE AUTH] Error code:', error.code);
        console.error('[SUPABASE AUTH] Error message:', error.message);
        console.error('[SUPABASE AUTH] Error status:', error.status);
        throw error;
      }

      // Success - Supabase ALWAYS returns success even if email doesn't exist
      // This is a security feature to prevent email enumeration
      console.log('[SUPABASE AUTH] ✅ Password reset request completed successfully');
      console.log('[SUPABASE AUTH] Email will be sent if account exists');
      console.log('[SUPABASE AUTH] ========================================');
      return data;
    } catch (error: any) {
      console.error('[SUPABASE AUTH] ❌ Password reset EXCEPTION:', error);
      console.error('[SUPABASE AUTH] Exception type:', typeof error);
      console.error('[SUPABASE AUTH] Exception keys:', Object.keys(error || {}));
      setError(handleAuthError(error));
      throw error;
    }
  };

  // Update password
  const updatePassword = async (newPassword: string) => {
    try {
      setError(null);
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;
    } catch (error: any) {
      setError(handleAuthError(error));
      throw error;
    }
  };

  // Update user profile
  const updateProfile = async (data: { firstName?: string; lastName?: string }) => {
    try {
      setError(null);
      const { error } = await supabase.auth.updateUser({
        data: {
          first_name: data.firstName,
          last_name: data.lastName,
        },
      });

      if (error) throw error;
    } catch (error: any) {
      setError(handleAuthError(error));
      throw error;
    }
  };

  const value = {
    user,
    session,
    loading,
    sessionReady,
    error,
    scenario: getUserScenario(user?.email, user?.user_metadata),
    userRole,
    foundersCircle,
    signUp,
    signIn,
    signInWithOAuth,
    signInWithMagicLink,
    signInWithPhone,
    verifyPhoneOtp,
    signOut,
    resetPassword,
    updatePassword,
    updateProfile,
    isBetaAuthenticated,
    betaUserEmail,
    clearError: () => setError(null),
    isAuthenticated: !!session || isBetaAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook to use auth context
export const useSupabaseAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useSupabaseAuth must be used within SupabaseAuthProvider');
  }
  return context;
};

// Export for backward compatibility with existing code
export const useAuth = useSupabaseAuth;